import {ETextParamType_IsUseGamePad} from './const';
import {getGyroActiveToggle, getVideoGainNode, setGyroActiveToggle} from './dataOps';
import {getVideo} from './domOps';
import {hel_setText, sendGamepadData} from './moduleCaller';

/**
 * @remarks
 * Videoの音声をセット
 * @param volume {number}
 */
export const hel_video_SetVolume = (volume: number) => {
  const video = getVideo();
  if (video === undefined) {
    return;
  }

  const gainNode = getVideoGainNode();
  if (gainNode === undefined) {
    // iOS/Mac以外ではgainNodeが生成されていなくてundefinedになるため、直接video要素に設定する
    video.volume = volume;
    return;
  }
  // globalThis.g_Video.volume = volume;
  gainNode.gain.value = volume;
};

/**
 * [ GYRO ]
 */
const requestToToggleActivateGyro = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('ActiveGyro', null, ['number'], [getGyroActiveToggle() ? 1 : 0]);
  }
};

const toggleGyroActive = (): boolean => {
  setGyroActiveToggle(!getGyroActiveToggle());

  requestToToggleActivateGyro();

  return getGyroActiveToggle();
};

export type GamepadInfo = {
  index: number;
  name: string;
  buttons: readonly GamepadButton[];
  axis: readonly number[];
};

/**
 * ゲームパッドコントローラーを取得
 */
const getGamepadControllers = (): Gamepad[] => {
  if (!globalThis.g_gamepadController) {
    throw new Error('g_gamepadController is not defined');
  }
  return globalThis.g_gamepadController;
};

/**
 * ゲームパッドコントローラーInfoを取得
 */
const getGamepadInfoArray = (): GamepadInfo[] => {
  if (!globalThis.g_gamepadInfoArray) {
    throw new Error('g_gamepadInfoArray is not defined');
  }
  return globalThis.g_gamepadInfoArray;
};

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var g_gamepadController: Gamepad[] | undefined;
  var g_gamepadInfoArray: GamepadInfo[] | undefined;
  /* eslint-enable */
}

Object.assign(
  globalThis,
  {
    g_gamepadController: [],
    g_gamepadInfoArray: []
  } satisfies Partial<typeof globalThis>,
  {
    /**
     * @remarks
     * ジャイロを変更
     * XRWorldIngame, JRWorld_web, WorldViewerにて未使用
     */
    toggleGyroActiveRequest: () => {
      if (getGyroActiveToggle()) {
        toggleGyroActive();
        return;
      }

      if (
        DeviceOrientationEvent &&
        (DeviceOrientationEvent as any).requestPermission &&
        typeof (DeviceOrientationEvent as any).requestPermission === 'function'
      ) {
        (DeviceOrientationEvent as any)
          .requestPermission()
          .then((permissionState: string) => {
            if (permissionState === 'granted') {
              toggleGyroActive();
            }
          })
          .catch(console.error);
      } else {
        toggleGyroActive();
      }
    },

    getGamepadControllers,

    getGamepadInfoArray
  }
);

const scanGamepads = () => {
  const controllers = getGamepadControllers();
  const gamepads = window.navigator.getGamepads
    ? window.navigator.getGamepads()
    : (window.navigator as any).webkitGetGamepads
      ? (window.navigator as any).webkitGetGamepads()
      : [];
  for (let i = 0; i < gamepads.length; i++) {
    if (gamepads[i] && gamepads[i].index in controllers) {
      controllers[gamepads[i].index] = gamepads[i];
    }
  }
};

export const updateGamepadsInfo = () => {
  // シーンファイルでusegamepadが有効になっていないのなら処理を行わない
  if (hel_setText(ETextParamType_IsUseGamePad, '') !== 1) {
    return;
  }

  scanGamepads();

  const controllers = getGamepadControllers();
  const gamepadInfoArray = getGamepadInfoArray();
  for (const [j, controller] of controllers.entries()) {
    if (!controller) {
      continue;
    }
    gamepadInfoArray[j] = {
      buttons: controller.buttons,
      axis: controller.axes,
      index: j,
      name: controller.id
    };
  }

  if (gamepadInfoArray[0] != null) {
    const gamepadInfo = gamepadInfoArray[0];
    sendGamepadData(
      gamepadInfo.buttons[0]?.value ?? 0,
      gamepadInfo.buttons[1]?.value ?? 0,
      gamepadInfo.buttons[2]?.value ?? 0,
      gamepadInfo.buttons[3]?.value ?? 0,
      gamepadInfo.buttons[4]?.value ?? 0,
      gamepadInfo.buttons[5]?.value ?? 0,
      gamepadInfo.buttons[6]?.value ?? 0,
      gamepadInfo.buttons[7]?.value ?? 0,
      gamepadInfo.buttons[8]?.value ?? 0,
      gamepadInfo.buttons[9]?.value ?? 0,
      gamepadInfo.buttons[10]?.value ?? 0,
      gamepadInfo.buttons[11]?.value ?? 0,
      gamepadInfo.buttons[12]?.value ?? 0,
      gamepadInfo.buttons[13]?.value ?? 0,
      gamepadInfo.buttons[14]?.value ?? 0,
      gamepadInfo.buttons[15]?.value ?? 0,
      gamepadInfo.axis[0] ?? 0,
      gamepadInfo.axis[1] ?? 0,
      gamepadInfo.axis[2] ?? 0,
      gamepadInfo.axis[3] ?? 0
    );
  }
};

const addGamepad = (e: GamepadEvent) => {
  const controllers = getGamepadControllers();
  controllers[e.gamepad.index] = e.gamepad;
};

const removeGamepad = (e: GamepadEvent) => {
  const controllers = getGamepadControllers();
  const gamepadInfoArray = getGamepadInfoArray();
  delete controllers[e.gamepad.index];
  delete gamepadInfoArray[e.gamepad.index];
};

/**
 * @remarks
 * ゲームパッドを扱う
 */
export const initializeGamePad = () => {
  if ('GamepadEvent' in window) {
    window.addEventListener('gamepadconnected', addGamepad);
    window.addEventListener('gamepaddisconnected', removeGamepad);
  } else if ('WebKitGamepadEvent' in window) {
    // @ts-ignore
    window.addEventListener('webkitgamepadconnected', addGamepad as any);
    // @ts-ignore
    window.addEventListener('webkitgamepaddisconnected', removeGamepad as any);
  }
};
